import React, { useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';

import { CloseCircleFilled, UploadOutlined } from '@ant-design/icons';
import { Form } from 'antd';
import styled from 'styled-components';

import { deleteCurrentUserAvatar, updateCurrentUserAvatar } from 'apis/UserAPI';
import Button from 'components/lib/Button';
import Modal from 'components/lib/Modal';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { handleUpdateAvatar } from 'store/slices/authSlice';

const StyledModal = styled(Modal)`
  .ant-modal-content {
    overflow: hidden;
  }

  .ant-modal-body {
    padding: 0 30px 30px;
  }

  .ant-modal-footer {
    padding: 20px 30px;
  }
`;

const FormContent = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 24px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UploadSection = styled(Section)`
  padding-bottom: 12px;
`;

const Description = styled.p`
  margin: 0;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const ErrorText = styled.p`
  margin: 0;
  color: var(--red);
  line-height: 1.4;
`;

const PreviewCard = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 300px;
  flex: 0 0 300px;
  box-sizing: border-box;
  overflow: hidden;
  padding: 24px;
  border: 1px solid var(--line-gray);
  border-radius: 8px;
  background: var(--light-gray);
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  border-radius: 6px;
`;

const EmptyState = styled.div`
  font-family: var(--font-bold);
  color: var(--text-secondary);
`;

const RemovePreviewButton = styled(Button)`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
`;

const DropzoneButton = styled.button<{ isDragActive: boolean }>`
  width: 100%;
  border: 1px dashed ${({ isDragActive }) => (isDragActive ? 'var(--button-blue)' : 'var(--line-gray)')};
  border-radius: 8px;
  background: ${({ isDragActive }) => (isDragActive ? 'var(--faded-blue)' : 'var(--white)')};
  padding: 28px 24px;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background 0.2s ease;
`;

const DropzoneContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
`;

const UploadIconWrapper = styled.div`
  color: var(--button-blue);
  font-size: 32px;
  line-height: 1;
`;

const UploadTitle = styled.div`
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const UploadSubtitle = styled.div`
  color: var(--text-secondary);
  line-height: 1.5;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 10px;
`;

interface Props {
  open: boolean;
  handleClose: () => void;
  title?: string;
}

const ChangeAvatarModal: React.FC<Props> = ({ open, handleClose, title = '' }) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [avatarSrc, setAvatarSrc] = useState(currentUser?.avatar || '');
  const [removeCurrentAvatar, setRemoveCurrentAvatar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setAvatarSrc(currentUser?.avatar || '');
    setRemoveCurrentAvatar(false);
  }, [currentUser?.avatar, open]);

  useEffect(() => {
    if (!file) {
      setPreview('');
      return;
    }

    const url = URL.createObjectURL(file);
    setPreview(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const onUpload = (files: File[]) => {
    setErrorMessage('');

    if (files.length === 0) {
      return;
    }

    const selectedFile = files[0];

    if (selectedFile.type.includes('image/')) {
      setFile(selectedFile);
      setRemoveCurrentAvatar(false);
      return;
    }

    setFile(null);
    setErrorMessage('Please upload only image files.');
  };

  const handleCloseModal = () => {
    if (loading) {
      return;
    }

    setFile(null);
    setAvatarSrc(currentUser?.avatar || '');
    setRemoveCurrentAvatar(false);
    setErrorMessage('');
    handleClose();
  };

  const onFinish = () => {
    if (!file && !removeCurrentAvatar) {
      return;
    }

    setLoading(true);

    if (file) {
      updateCurrentUserAvatar(file)
        .then((res) => {
          dispatch(handleUpdateAvatar(res.avatar || ''));
          setAvatarSrc(res.avatar || '');
          setLoading(false);
          handleCloseModal();
        })
        .catch(() => {
          setLoading(false);
        });
      return;
    }

    deleteCurrentUserAvatar()
      .then(() => {
        dispatch(handleUpdateAvatar(''));
        setAvatarSrc('');
        setRemoveCurrentAvatar(false);
        setLoading(false);
        handleCloseModal();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleRemoveSelectedFile = () => {
    setFile(null);
    setErrorMessage('');
  };

  const handleRemoveCurrentPhoto = () => {
    setFile(null);
    setPreview('');
    setRemoveCurrentAvatar(true);
    setErrorMessage('');
  };

  const previewSrc = file ? preview : removeCurrentAvatar ? '' : avatarSrc;
  const showRemovePhotoAction = Boolean(avatarSrc) && !removeCurrentAvatar && !file;
  const footer = (
    <Footer>
      <FooterActions>
        {showRemovePhotoAction && (
          <Button type="default" danger htmlType="button" onClick={handleRemoveCurrentPhoto} disabled={loading}>
            Remove Photo
          </Button>
        )}
        <Button type="default" htmlType="button" onClick={handleCloseModal} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          form="change-avatar-form"
          loading={loading}
          disabled={loading || (!file && !removeCurrentAvatar)}
        >
          Save
        </Button>
      </FooterActions>
    </Footer>
  );

  return (
    <>
      <StyledModal centered open={open} onCancel={handleCloseModal} width={560} footer={footer} title={title}>
        <FormContent
          id="change-avatar-form"
          onFinish={onFinish}
          layout="vertical"
          requiredMark={false}
          autoComplete="off"
        >
          <Section>
            <Description>Upload a new profile image. PNG, JPG, and other image formats are supported.</Description>
            {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
          </Section>

          <Section>
            <PreviewCard>
              {previewSrc ? <PreviewImage src={previewSrc} alt="Avatar preview" /> : <EmptyState>No Image</EmptyState>}
              {previewSrc && file && (
                <RemovePreviewButton
                  type="default"
                  danger
                  icon={<CloseCircleFilled />}
                  htmlType="button"
                  onClick={handleRemoveSelectedFile}
                />
              )}
            </PreviewCard>
          </Section>

          <UploadSection>
            <Dropzone onDrop={(acceptedFiles) => onUpload(acceptedFiles)} multiple={false}>
              {({ getRootProps, getInputProps, isDragActive }) => (
                <div {...getRootProps()}>
                  <input {...getInputProps({ accept: 'image/*' })} />
                  <DropzoneButton type="button" isDragActive={isDragActive}>
                    <DropzoneContent>
                      <UploadIconWrapper>
                        <UploadOutlined />
                      </UploadIconWrapper>
                      <UploadTitle>{file ? 'Replace selected image' : 'Click or drag an image to upload'}</UploadTitle>
                      <UploadSubtitle>
                        {file ? file.name : 'Choose a file from your device, or drop it here.'}
                      </UploadSubtitle>
                    </DropzoneContent>
                  </DropzoneButton>
                </div>
              )}
            </Dropzone>
          </UploadSection>
        </FormContent>
      </StyledModal>
    </>
  );
};

export default ChangeAvatarModal;
